import { parseSiteAddressLine, type SiteAddressParts } from '@evi/shared';
import type { JobProduct, NetworkPriceView } from './partnerJobs';
import type { PartnerCustomer } from './partnerCustomers';
import { planPriceLine } from './planPrices';

/**
 * The New job screen's rules (Warren, 21 October 2026: one screen, clicks and fields way down). The address is one
 * line, the customer a name and an email, the hardware charger cards with a quantity each, and the network plan yearly
 * unless switched to monthly. Pure, so the counts and picks are unit-tested (newJobForm.test.ts).
 */
export interface QtyLine { product_id: string; quantity: number }
export const MAX_QUANTITY = 1000;

const isCharger = (p: JobProduct) => p.charger ?? p.tier !== 'accessory';

/** The order partners reach for chargers in (Warren, 21 October 2026: commercial first, the ones partners order most). */
const TIER_ORDER = ['commercial_ac', 'dc', 'high_power', 'home'];
const tierRank = (p: JobProduct, home: boolean) => (home && p.tier === 'home' ? -1 : (i => (i < 0 ? TIER_ORDER.length : i))(TIER_ORDER.indexOf(p.tier)));

/**
 * The hardware cards (Warren, 21 October 2026, walkthrough A 037, A 038): the commercial chargers first, cheapest first
 * within each kind, so a phone shows them before anything else; home units only on a home drop-ship (where they come
 * first); everything else (the DC chargers, quote-only sets, pedestals and cables) under More hardware. A product sold
 * by quote never takes a quantity: it reads "Quote only: ask EVI". A product already on the job always shows.
 */
export function hardwareCards(products: JobProduct[], siteType?: string | null, chosen: readonly string[] = []) {
  const home = siteType === 'home_drop_ship';
  const shown = products.filter(p => chosen.includes(p.id) || home || p.tier !== 'home');
  const sorted = [...shown].sort((a, b) => Number(!!a.quote_only) - Number(!!b.quote_only) || tierRank(a, home) - tierRank(b, home) || a.price_cents - b.price_cents || a.name.localeCompare(b.name));
  const first = home ? 'home' : 'commercial_ac';
  let chargers = sorted.filter(p => isCharger(p) && !p.quote_only && p.tier === first);
  if (!chargers.length) chargers = sorted.filter(p => isCharger(p) && !p.quote_only);
  return { chargers, more: sorted.filter(p => isCharger(p) && !chargers.includes(p)), other: sorted.filter(p => !isCharger(p)) };
}

export const quantityOf = (lines: QtyLine[], productId: string) => lines.find(l => l.product_id === productId)?.quantity ?? 0;

/** One product's quantity; zero removes its line, and a new product joins the end so the order stays as tapped. */
export function setQuantity(lines: QtyLine[], productId: string, quantity: number): QtyLine[] {
  const q = Math.max(0, Math.min(MAX_QUANTITY, Math.floor(quantity)));
  if (!q) return lines.filter(l => l.product_id !== productId);
  return lines.some(l => l.product_id === productId) ? lines.map(l => (l.product_id === productId ? { ...l, quantity: q } : l)) : [...lines, { product_id: productId, quantity: q }];
}

/** The hardware subtotal at list price, and the chargers the network plan counts. */
export function hardwareTotals(lines: QtyLine[], products: JobProduct[]) {
  let subtotal = 0, chargers = 0;
  for (const l of lines) {
    const p = products.find(x => x.id === l.product_id);
    if (!p) continue;
    subtotal += p.price_cents * l.quantity;
    if (isCharger(p)) chargers += l.quantity;
  }
  return { subtotal_cents: subtotal, chargers };
}

/**
 * The saved customers offered as one-tap picks on New job (a repeat job is the customer, the chargers and Submit):
 * the most recently changed first, narrowed by what is typed in the name field, archived ones never.
 */
export function customerPicks(customers: PartnerCustomer[], typed: string, limit = 5): PartnerCustomer[] {
  const q = typed.trim().toLowerCase();
  const match = (c: PartnerCustomer) => !q || [c.business_name, c.contact_name, c.contact_email, ...c.sites.map(s => s.street)].some(v => (v ?? '').toLowerCase().includes(q));
  return customers.filter(c => !c.archived && match(c)).sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, limit);
}

/** What a typed address line is read as (the Api reads it the same way); a line that cannot be read stays the street. */
export function addressFromLine(line: string): SiteAddressParts {
  return parseSiteAddressLine(line) ?? { street: line.trim(), city: '', state: '', zip: '', country: '' };
}
/** The one line the address field shows for parts it already has (a saved site, or a job being edited). */
export function lineFromAddress(p: Partial<SiteAddressParts>): string {
  return [p.street, p.city, [p.state, p.zip].filter(Boolean).join(' '), p.country].map(x => (x ?? '').trim()).filter(Boolean).join(', ');
}
/** Complete enough for EVI's review: the street, city, state, postal code and country. */
export const addressComplete = (p: Partial<SiteAddressParts>) => ['street', 'city', 'state', 'zip', 'country'].every(k => !!(p as Record<string, string | undefined>)[k]?.trim());

/**
 * The collapsed hardware section's summary, Warren's words verbatim (21 October 2026, night): "More hardware (DC
 * chargers, quote-only sets, pedestals and cables)". The label, then its detail in a lighter span.
 */
export const MORE_HARDWARE_SUMMARY = { label: 'More hardware', detail: '(DC chargers, quote-only sets, pedestals and cables)' } as const;

/**
 * The plan's price per charger, as the partner reads it: "US$80.00 per charger per year", each cadence from its own
 * Stripe Price, or "EV Initiative confirms the price" where Stripe has none (Warren, 21 October 2026, night).
 */
export function networkPriceLine(price: NetworkPriceView | null | undefined, format: (cents: number, currency: string) => string): string {
  return planPriceLine(price, format);
}

/** "What happens next" on New job: Warren's six steps, verbatim and numbered as he wrote them (21 October 2026, evening). */
export const NEW_JOB_NEXT_STEPS = [
  'Submit: approved at once when every check passes, otherwise EVI reviews it within one business day.',
  'You or your customer pay for hardware and freight in one checkout.',
  'Autel programs each charger with its EVI ID.',
  'Shipped with tracking.',
  'Install and power up: the chargers come online by themselves, then finish the plug-in and label tests.',
  'Launch is automatic once every step is done, and your rate is stamped on each charger.',
] as const;

/**
 * What New job starts with before the Api's defaults arrive (Warren, 21 October 2026: EVI works out the rest): a
 * workplace site, the customer running the chargers as the charger owner. The quote builder starts from the same, so a
 * quote needs no tap New job does not (walkthrough counts, step 049).
 */
export const NEW_JOB_PRESETS = { site_type: 'workplace', customer_role: 'charger_owner' } as const;
/** "Who runs the chargers after install: your customer, as the charger owner", with a Change link beside it. */
export function customerRoleLine(role: string | null, labels: Record<string, string>): string {
  return role === 'charger_owner' ? 'your customer, as the charger owner' : role ? labels[role] ?? role : 'not chosen';
}

/** The Chargers card's price line, by who pays (walkthrough counts, step 038): the customer pays EVI at MSRP and the
 * partner bills its own work directly (Warren, final ruling 2, 21 October 2026); a partner who pays sets its own price. */
export const chargersPriceLine = (customerPays: boolean) => (customerPays
  ? 'Your customer pays EVI at MSRP. You bill your own work directly.' : 'List price. You set your own price to the customer.');

/** The banner on a new partner's first New job. Payout setup is asked for only when a commission is owed (Warren, 21
 * October 2026), so it says when EVI asks, never that payouts wait on My Installs (walkthrough counts, step 032). */
export const NEW_JOB_WELCOME = 'Welcome to EV Initiative. Start your first job here. Your team and your profile are on My Installs whenever you want them. '
  + 'EVI asks for your payout details when your first commission is owed.';

/**
 * Whether the address is far enough along to price the freight (Warren, 21 October 2026, evening: "As soon as the
 * address is entered, freight is priced live"): a whole US ZIP, or a whole Canadian postal code. Never a ZIP half
 * typed, so no keystroke reaches the carrier (the Api also keeps each answer while it stands).
 */
export function freightAskable(site: { zip: string; country: string }) {
  const zip = site.zip.trim(), country = (site.country || 'US').toUpperCase();
  return country === 'CA' ? /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i.test(zip) : /^\d{5}(-\d{4})?$/.test(zip);
}

/** The carrier option the price is for: the partner's pick while it is offered (by its id, else the same carrier and
 * service by its key), else the first, the cheapest, preselected. */
export function chosenFreightOption<T extends { id: string; key: string }>(options: readonly T[] | undefined, pick: { option_id?: string | null; key?: string | null } | null | undefined): T | null {
  if (!options?.length) return null;
  return options.find(o => !!pick?.option_id && o.id === pick.option_id) ?? options.find(o => !!pick?.key && o.key === pick.key) ?? options[0]!;
}

/** One carrier option as a compact choice: "Estes Express, 3 days, $585.07" (transit, or the date the carrier commits to). */
export function freightOptionText(o: { carrier: string; transit_days: number | null; estimated_delivery_date?: string | null; price_cents?: number }, format: (cents: number) => string) {
  const transit = o.transit_days != null ? `${o.transit_days} day${o.transit_days === 1 ? '' : 's'}` : o.estimated_delivery_date
    ? `by ${new Date(`${o.estimated_delivery_date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}` : null;
  return [o.carrier, transit, o.price_cents != null ? format(o.price_cents) : null].filter(Boolean).join(', ');
}

/**
 * The New job total (Warren, 21 October 2026, evening): "As soon as the address is entered, freight is priced live and
 * included in the total, as one line, 'Hardware shipped to location'." On the manual path the line is "Freight quote
 * within one business day" and the total says freight follows. Before an address, the hardware alone.
 */
export function newJobTotal(hardwareCents: number, freight: { outcome: 'priced' | 'manual'; line: string; price_cents?: number } | null | undefined,
  chosenCents: number | null | undefined, format: (cents: number) => string) {
  const cents = chosenCents ?? freight?.price_cents;
  if (freight?.outcome === 'priced' && cents != null) return { total_cents: hardwareCents + cents, label: 'Hardware and freight', freight_line: `${freight.line}: ${format(cents)}`, follows: false };
  if (freight?.outcome === 'manual') return { total_cents: hardwareCents, label: 'Hardware, freight follows', freight_line: freight.line, follows: true };
  return { total_cents: hardwareCents, label: 'Hardware at list price', freight_line: freight?.line ?? null, follows: false };
}
